/**
 * The navigation state, and how it is written into the query string.
 *
 * This is the successor to `urlState.ts`, which held the same job for two surfaces and grew a
 * documented workaround for each new one. The shape here is a **codec registry**: every surface
 * declares the params it owns and how to read and write them, and the core knows nothing about any
 * of them. Adding Review or Grammar is one codec plus one field on `NavState` — which is exactly
 * what Stats cost.
 *
 * **Query params rather than paths.** Caddy's SPA fallback would serve `/cards` happily, but
 * `/api/*`, `/sync*`, `/bulk/*`, `/oauth/*`, `/mcp*`, `/healthz` and `/.well-known/oauth-*` are all
 * routed above it — so a path scheme buys a permanent "never name a surface any of those" rule and a
 * Caddyfile edit. Params cost neither. They also survive signing in for free: `currentNext()` is
 * `pathname + search`, and the server only requires a leading `/`.
 *
 * **Params are namespaced by surface** (`cards.deck`, `read.log`, `setup.job`); only `tab` is bare,
 * because it belongs to no surface. The names share one flat space, so a collision would be silent —
 * the namespace makes it impossible by construction and `navUrl.test.ts` asserts the codecs claim
 * disjoint sets.
 *
 * **Only the surface you are ON writes to the URL.** Every surface used to, which put Cards' deck
 * and Read's log id on a Setup URL — state unrelated to the page, in an address bar somebody reads
 * and pastes. Alex, 6 Aug 2026, after seeing it on staging. What the URL stops carrying does not go
 * anywhere near a history entry: `history.state` holds the full snapshot, which is what Back and
 * Forward actually read, and the inactive surfaces' state is kept per tab in `navSession.ts` so a
 * refresh still loses nothing. **Parsing stays total** — a hand-edited or older URL naming another
 * surface's params is still honoured, it just will not be rewritten that way.
 *
 * **The capture params are the exception and stay bare.** `q`, `image`, `slot` and `n` are the tray
 * agent's wire format — `Nihongonator.Host` builds those URLs — so they are not ours to rename.
 * They are consumed and stripped at boot; see `withoutCaptureQuery`.
 *
 * **What is here is what survives a refresh.** Anything navigation-grade that must not survive one
 * — an open lightbox, the radical picker, a half-finished "move this sentence" — is deliberately
 * absent from the codecs and lives only in `history.state`. See `navHistory.ts`.
 */

import {
  CARD_STATES,
  EMPTY_FILTERS,
  FLAGS,
  type CardFilters,
  type CardState,
} from "@/surfaces/cards/query";
import { DESTINATIONS, type DestinationId } from "./destinations";

/**
 * Cards' filters as the URL knows them, which is `CardFilters` plus two distinctions the in-memory
 * shape does not need to make.
 */
export interface CardsUrlState extends Omit<CardFilters, "deck"> {
  /**
   * **`undefined` and `null` are different states and this whole type exists to keep them apart.**
   *
   * `undefined` — the param was ABSENT. You have never chosen a deck, so Cards seeds the vocab
   * default; arriving on an unfiltered `All` returns no rows by design and reads as breakage.
   *
   * `null` — the param was PRESENT and empty (`?cards.deck=`). You chose **All**, on purpose.
   * Collapse this into `undefined` and every refresh while on All snaps back to Vocab.
   *
   * `?cards.deck=` round-trips through `URLSearchParams` intact and, unlike a sentinel string,
   * cannot collide with a deck someone actually named.
   */
  deck: string | null | undefined;
  /** The note whose dock is open. `null` is row 0, which is where an arrival with no note lands. */
  note: number | null;
}

/** Read's view state: which of the surface's two faces is showing, and onto what. */
export interface ReadUrlState {
  view: "log" | "library";
  /** The log being read. A mirror of `useReading`'s `activeLogId` — see `App`'s two sync effects. */
  log: string | null;
  /**
   * A sentence mid-`Move to log ▸ Choose from library…`.
   *
   * Travels with `view` and is **deliberately not serialized**: it is an intent, not a place, and a
   * refresh landing you in the library with a stale sentence half-moved is worse than one that
   * simply drops it.
   */
  moving: string | null;
}

/** Setup's two drill-ins. `jobId` is phone-only; `historyOpen` swaps a panel on desktop. */
export interface SetupUrlState {
  jobId: string | null;
  historyOpen: boolean;
  /** Phone-only, like `jobId`: the deck whose options page is open (日本-344). On a desktop the
   * form opens in place under its row, which is churn rather than a place and stays local. */
  deckId: number | null;
}

/** The four Stats tabs, in the order STATS.md lists them. */
export const STATS_TABS = ["history", "recall", "coverage", "pace"] as const;
export type StatsTab = (typeof STATS_TABS)[number];

/**
 * Stats' view state: which tab, and over which deck.
 *
 * **The deck is a top-level deck, and it scopes all four tabs** (Alex, 8 Aug 2026) — the design
 * draws `Japanese::Vocab ·` as static header text, but in the build it is a control. `null` means
 * "never chosen", and the server answers which deck to land on; unlike `cards.deck` there is no
 * meaningful "All", because a statistic over every deck at once would mix a Japanese vocabulary
 * deck with whatever else the collection happens to hold.
 */
export interface StatsUrlState {
  tab: StatsTab;
  deck: string | null;
}

/**
 * Search's whole state: the query. (日本-497)
 *
 * **In the URL, but it never earns a history entry.** A search is something you paste and something
 * a refresh must not lose, so it is serialized — and it is typed one character at a time, which is
 * precisely the churn `navHistory.ts` refuses to put in the stack. So it is deliberately absent from
 * `classify`'s push list and falls through to `replace`, exactly as `cards.find` does. It is present
 * in `sameState` for the other half of that: omitted there, every keystroke would compare equal and
 * the URL would never be written at all.
 *
 * `""` rather than `null` for the empty state — one fact, one spelling, on `ReadingLog.name`'s
 * argument in reverse. There is no meaningful difference here between "never searched" and
 * "searched for nothing", and a nullable field would invent one.
 */
export interface SearchUrlState {
  q: string;
}

/** 文法 (日本-519). `entry` is a place; the rest refine it — see `grammarCodec`. */
export interface GrammarUrlState {
  q: string;
  /** `Basic` | `Intermediate` | `Advanced`, or null for 全巻. The SOURCE value, not the label. */
  volume: string | null;
  /** A part-of-speech HEAD, as the source spells it (`Phrase`), not as 文法 draws it. */
  partOfSpeech: string | null;
  /** The DoJG id of the open entry (`basic-079`), or null with nothing expanded. */
  entry: string | null;
}

/**
 * Everywhere you can be. One object, held by `App`, and the only thing the URL and the history
 * stack ever carry.
 */
export interface ReviewUrlState {
  /**
   * The deck a sitting is open on, or null for the landing screen.
   *
   * **That is the whole of Review's URL state, and it is enough on purpose.** The queue lives on
   * the server, so a reload posts `/api/review/start` for this deck and resumes exactly where it
   * was — the card, the tally and the undo stack all belong to the sitting rather than to the page.
   * Which card is on screen is deliberately absent: it is churn, and a URL that named one would
   * restore a card the scheduler has since moved on from.
   */
  deck: string | null;
}

/**
 * 混同 (日本-601). Three places and one set of refinements.
 *
 * `group` and `drill` are **places** and push: opening a group is somewhere Back returns from, and a
 * drill is a sitting Back leaves. `drill` names the chosen groups rather than the cards, so a reload
 * restarts the run on the same groups — a drill is its own run and keeps nothing, so that is the
 * whole of what there is to restore.
 *
 * `cards` is the group page's own copy of 単語's filters, under `groups.`-prefixed params. **Not a
 * view of `NavState.cards`**: browsing a group must never change what 単語 shows when you go back to
 * it. Its `deck` is unused — a group spans decks.
 */
export interface GroupsUrlState {
  group: string | null;
  drill: string[] | null;
  cards: CardsUrlState;
}

export interface NavState {
  /**
   * Where you are. **Always a real destination** since Mining's removal took the phone's `More`
   * sheet with it (8 Aug 2026).
   *
   * This used to be `DestinationId | "more"`, with a `lastRealTab` beside it holding where the URL
   * pointed while the sheet was open and where closing it returned you. `more` was a tab that was
   * not a place — `destination()` threw on it, it could not be serialized, and every reader had to
   * remember to resolve it. Five destinations fit across a phone, so the sheet is gone and with it
   * the whole special case.
   */
  tab: DestinationId;
  cards: CardsUrlState;
  read: ReadUrlState;
  review: ReviewUrlState;
  search: SearchUrlState;
  grammar: GrammarUrlState;
  setup: SetupUrlState;
  stats: StatsUrlState;
  groups: GroupsUrlState;
}

export const EMPTY_CARDS_URL_STATE: CardsUrlState = {
  ...EMPTY_FILTERS,
  deck: undefined,
  note: null,
};

export const DEFAULT_NAV_STATE: NavState = {
  tab: "read",
  cards: EMPTY_CARDS_URL_STATE,
  read: { view: "log", log: null, moving: null },
  review: { deck: null },
  search: { q: "" },
  grammar: { q: "", volume: null, partOfSpeech: null, entry: null },
  setup: { jobId: null, historyOpen: false, deckId: null },
  stats: { tab: "history", deck: null },
  groups: { group: null, drill: null, cards: EMPTY_CARDS_URL_STATE },
};

/**
 * The capture deep link's params, owned by `App`'s boot and deleted once it has run.
 *
 * All four, not just `q`. Serialization deliberately preserves params it does not own, so anything
 * left here would survive every later write for the life of the tab — and a reload would then
 * re-attach an hour-old screenshot to a brand-new sentence, the same way a surviving `q` used to
 * re-add a line you had already read past. `n` is the agent's cache-buster and means nothing to
 * this app, but it only ever appears alongside `q` and costs nothing to sweep with it.
 */
const CAPTURE_PARAMS = ["q", "image", "slot", "n"] as const;

/**
 * One surface's half of the query string.
 *
 * `parse` returns a slice of `NavState`; unknown values are **dropped, never thrown on**, because a
 * hand-edited or truncated URL must not put garbage into a typed field — `tab` above all, which
 * would otherwise reach `destination()` and throw.
 *
 * `serialize` is called with every owned param already deleted, so it only ever adds. Omitting a
 * default keeps a fresh session's address bar bare.
 */
interface SurfaceCodec {
  /** Names this codec's slice, for `surfacesInSearch` and the per-tab session store. */
  key: SurfaceKey;
  /**
   * The destination this codec belongs to. `undefined` means "always", which is the core's alone —
   * every other codec writes only while its own surface is the one on screen.
   */
  tab?: DestinationId;
  owned: readonly string[];
  parse(params: URLSearchParams): Partial<NavState>;
  serialize(state: NavState, params: URLSearchParams): void;
}

export type SurfaceKey =
  | "core"
  | "cards"
  | "grammar"
  | "groups"
  | "read"
  | "review"
  | "search"
  | "setup"
  | "stats";

function splitList(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

const coreCodec: SurfaceCodec = {
  key: "core",
  owned: ["tab"],
  parse(params) {
    const tab = params.get("tab");
    const known = DESTINATIONS.some((d) => d.id === tab);
    if (!known) return {};
    return { tab: tab as DestinationId };
  },
  serialize(state, params) {
    // `read` is the default, so it is omitted — a fresh session's URL stays bare.
    if (state.tab !== "read") params.set("tab", state.tab);
  },
};

/**
 * **Closed vocabularies comma-join; free strings repeat.** `cards.state` and `cards.flag` are fixed
 * sets, so `cards.state=new,suspended` cannot be ambiguous. A tag name is a free string that may
 * contain a comma, so tags are a repeated param read with `getAll`.
 */
/** 単語's filters under a param prefix — `cards.` for 単語 itself, `groups.` for a group's copy. */
function parseCardFilters(params: URLSearchParams, prefix: string): CardsUrlState {
  const note = Number(params.get(`${prefix}.note`));
  return {
    // `has` and not `get`: present-and-empty is the "All" answer, absence is "never chosen".
    deck: params.has(`${prefix}.deck`) ? params.get(`${prefix}.deck`) || null : undefined,
    states: splitList(params.get(`${prefix}.state`)).filter((s): s is CardState =>
      (CARD_STATES as readonly string[]).includes(s),
    ),
    // Validated against `FLAGS` rather than a hand-written `[1,2,3,4,5]`: the chips are built
    // from that same table, and a list only this file knew about would let a chip you can click
    // be silently dropped on the way back in.
    flags: splitList(params.get(`${prefix}.flag`))
      .map(Number)
      .filter((f) => FLAGS.some((flag) => flag.value === f)),
    tags: params.getAll(`${prefix}.tag`).filter(Boolean),
    text: params.get(`${prefix}.find`) ?? "",
    note: Number.isSafeInteger(note) && note > 0 ? note : null,
  };
}

function serializeCardFilters(
  cards: CardsUrlState,
  params: URLSearchParams,
  prefix: string,
  withDeck: boolean,
): void {
  if (withDeck && cards.deck !== undefined) params.set(`${prefix}.deck`, cards.deck ?? "");
  if (cards.states.length) params.set(`${prefix}.state`, cards.states.join(","));
  if (cards.flags.length) params.set(`${prefix}.flag`, cards.flags.join(","));
  for (const tag of cards.tags) params.append(`${prefix}.tag`, tag);
  if (cards.text) params.set(`${prefix}.find`, cards.text);
  if (cards.note !== null) params.set(`${prefix}.note`, String(cards.note));
}

const cardsCodec: SurfaceCodec = {
  key: "cards",
  tab: "cards",
  owned: ["cards.deck", "cards.state", "cards.flag", "cards.tag", "cards.find", "cards.note"],
  parse(params) {
    return { cards: parseCardFilters(params, "cards") };
  },
  serialize({ cards }, params) {
    serializeCardFilters(cards, params, "cards", true);
  },
};

const groupsCodec: SurfaceCodec = {
  key: "groups",
  tab: "groups",
  owned: [
    "groups.id",
    "groups.drill",
    "groups.state",
    "groups.flag",
    "groups.tag",
    "groups.find",
    "groups.note",
  ],
  parse(params) {
    const drill = splitList(params.get("groups.drill"));
    return {
      groups: {
        group: params.get("groups.id") || null,
        drill: drill.length ? drill : null,
        // No deck: a group spans decks, so `groups.deck` is neither read nor written.
        cards: { ...parseCardFilters(params, "groups"), deck: undefined },
      },
    };
  },
  serialize({ groups }, params) {
    if (groups.drill?.length) params.set("groups.drill", groups.drill.join(","));
    if (groups.group) {
      params.set("groups.id", groups.group);
      serializeCardFilters(groups.cards, params, "groups", false);
    }
  },
};

const readCodec: SurfaceCodec = {
  key: "read",
  tab: "read",
  owned: ["read.view", "read.log"],
  parse(params) {
    // `moving` is never serialized, so a parsed state always starts with no move in flight.
    return {
      read: {
        view: params.get("read.view") === "library" ? "library" : "log",
        log: params.get("read.log") || null,
        moving: null,
      },
    };
  },
  serialize({ read }, params) {
    if (read.view === "library") params.set("read.view", "library");
    if (read.log) params.set("read.log", read.log);
  },
};

/** A deck id, or nothing: a hand-edited `setup.deck=abc` must not reach the API as `NaN`. */
function deckParam(value: string | null): number | null {
  return value && /^\d+$/.test(value) ? Number(value) : null;
}

const setupCodec: SurfaceCodec = {
  key: "setup",
  tab: "setup",
  owned: ["setup.job", "setup.hist", "setup.deck"],
  parse(params) {
    return {
      setup: {
        jobId: params.get("setup.job") || null,
        historyOpen: params.get("setup.hist") === "1",
        deckId: deckParam(params.get("setup.deck")),
      },
    };
  },
  serialize({ setup }, params) {
    if (setup.jobId) params.set("setup.job", setup.jobId);
    if (setup.historyOpen) params.set("setup.hist", "1");
    if (setup.deckId !== null) params.set("setup.deck", String(setup.deckId));
  },
};

const reviewCodec: SurfaceCodec = {
  key: "review",
  tab: "review",
  owned: ["review.deck"],
  parse(params) {
    return { review: { deck: params.get("review.deck") || null } };
  },
  serialize({ review }, params) {
    if (review.deck) params.set("review.deck", review.deck);
  },
};

/**
 * 文法 (日本-519). Four params, and which of them PUSH is the design rather than a detail.
 *
 * `grammar.entry` is a **place** — it is what Read's 開く deep-links to and what a reader shares —
 * so opening an entry gets a history entry and Back closes it. The query and the two filters are
 * refinements of where you already are and therefore REPLACE, on `cards.find`'s reasoning: a
 * per-keystroke entry buries the one that leaves the app.
 */
const grammarCodec: SurfaceCodec = {
  key: "grammar",
  tab: "grammar",
  owned: ["grammar.q", "grammar.vol", "grammar.pos", "grammar.entry"],
  parse(params) {
    return {
      grammar: {
        q: params.get("grammar.q") ?? "",
        volume: params.get("grammar.vol") || null,
        partOfSpeech: params.get("grammar.pos") || null,
        entry: params.get("grammar.entry") || null,
      },
    };
  },
  serialize({ grammar }, params) {
    // Each omitted when empty, so arriving on a fresh 文法 leaves the address bar at `?tab=grammar`.
    if (grammar.q) params.set("grammar.q", grammar.q);
    if (grammar.volume) params.set("grammar.vol", grammar.volume);
    if (grammar.partOfSpeech) params.set("grammar.pos", grammar.partOfSpeech);
    if (grammar.entry) params.set("grammar.entry", grammar.entry);
  },
};

const searchCodec: SurfaceCodec = {
  key: "search",
  tab: "search",
  owned: ["search.q"],
  parse(params) {
    return { search: { q: params.get("search.q") ?? "" } };
  },
  serialize({ search }, params) {
    // Omitted when empty, so arriving on a fresh 検索 leaves the address bar at `?tab=search`.
    if (search.q) params.set("search.q", search.q);
  },
};

/**
 * Review is deliberately **not** in `navSession`'s per-tab store, which Cards, Read and Setup are.
 *
 * Those hold a filter or a place you were looking at, and restoring one is a convenience. This
 * holds an *open sitting*, and restoring it would start a review session on a rail click — from a
 * value that could be hours old and name a deck that is finished for the day. Leaving Review and
 * coming back lands on the landing screen; the server's sitting is still open, so pressing Study
 * resumes the same tally and the same undo stack. Stats sets the same precedent.
 */

const statsCodec: SurfaceCodec = {
  key: "stats",
  tab: "stats",
  owned: ["stats.tab", "stats.deck"],
  parse(params) {
    const tab = params.get("stats.tab");
    return {
      stats: {
        // Validated against the tuple rather than cast: a hand-edited `stats.tab=retention` — the
        // name the retired three-tab design used — must fall back to History rather than render a
        // tab that does not exist.
        tab: (STATS_TABS as readonly string[]).includes(tab ?? "")
          ? (tab as StatsTab)
          : "history",
        deck: params.get("stats.deck") || null,
      },
    };
  },
  serialize({ stats }, params) {
    // History is the default, so it is omitted and a fresh `?tab=stats` stays short.
    if (stats.tab !== "history") params.set("stats.tab", stats.tab);
    if (stats.deck) params.set("stats.deck", stats.deck);
  },
};

/**
 * Every surface that puts anything in the URL.
 *
 * **A new surface is added here and nowhere else.** Give it a field on `NavState`, a codec with a
 * `<surface>.`-prefixed param list, and a default in `DEFAULT_NAV_STATE`; the parse, the write, the
 * "leave other params alone" rule and the history plumbing all follow for free.
 */
export const CODECS: readonly SurfaceCodec[] = [
  coreCodec,
  cardsCodec,
  grammarCodec,
  groupsCodec,
  readCodec,
  reviewCodec,
  searchCodec,
  setupCodec,
  statsCodec,
];

/** Every param this module owns. Anything else in the query string is somebody else's and is kept. */
export const OWNED_PARAMS: readonly string[] = CODECS.flatMap((codec) => codec.owned);

/** Read the navigation state out of a query string. Anything unreadable falls back to its default. */
export function parseNavUrl(search: string): NavState {
  const params = new URLSearchParams(search);
  let state = DEFAULT_NAV_STATE;
  for (const codec of CODECS) state = { ...state, ...codec.parse(params) };
  return state;
}

/**
 * Write it back, on top of whatever the query string already holds.
 *
 * Takes the **current** search rather than building from nothing, so a param this module does not
 * own survives — `q` above all, which is mid-flight at boot. Must be called with the live
 * `window.location.search` and never a value captured at mount, or a later write would resurrect the
 * `q` the capture path had just deleted.
 *
 * Returns `""` rather than `"?"` when nothing is left, so the address bar of a fresh session stays a
 * bare `/`.
 */
export function serializeNavUrl(search: string, state: NavState): string {
  const params = new URLSearchParams(search);
  // Every owned param is cleared, not just the active surface's — that is what takes Cards' filters
  // out of the address bar when you leave Cards, rather than freezing them there.
  for (const name of OWNED_PARAMS) params.delete(name);
  const active = activeTab(state);
  for (const codec of CODECS) {
    if (codec.tab === undefined || codec.tab === active) codec.serialize(state, params);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/**
 * Where you are.
 *
 * Kept as a function rather than inlined as `state.tab`: it is called from three places, and it was
 * the one place that resolved the old `more` pseudo-tab. A future state that is again not simply a
 * destination has somewhere to be handled.
 */
export function activeTab(state: NavState): DestinationId {
  return state.tab;
}

/**
 * Which surfaces the query string actually speaks for.
 *
 * The session store fills in the surfaces a trimmed URL no longer names, so it has to know which
 * those are: a URL that *does* carry `cards.deck` — hand-edited, or a link from before this
 * changed — must beat whatever this tab happens to have stored.
 */
export function surfacesInSearch(search: string): Set<SurfaceKey> {
  const params = new URLSearchParams(search);
  const found = new Set<SurfaceKey>();
  for (const codec of CODECS) {
    if (codec.key === "core") continue;
    if (codec.owned.some((name) => params.has(name))) found.add(codec.key);
  }
  return found;
}

/**
 * Drop the capture params and keep the rest.
 *
 * Used once, at boot, before any history entry exists — which is what makes it impossible for Back
 * to ever land on a URL that still carries a capture.
 */
export function withoutCaptureQuery(search: string): string {
  const params = new URLSearchParams(search);
  for (const name of CAPTURE_PARAMS) params.delete(name);
  const query = params.toString();
  return query ? `?${query}` : "";
}
